import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { makeStyles } from '@material-ui/core/styles';
import { InputPropType, SelectDataPropType } from '../../proptypes/input';
import { ApiErrorPropType } from '../../proptypes/errors';
import { useIsFranchise, useGetUserEmplyeeId } from '../../Modules/abilities/hooks';
import { selectEmployeesById } from '../../Modules/employees/employeesSlice';

// Mirrors Modules/form/components/specialized/LocationMultiselect.js so both
// the Formik and redux-form flavoured location pickers look and behave the same.
export const UNASSIGNED_LOCATION = 'unassigned';

const useStyles = makeStyles(() => ({
    fullWidth: {
        width: '100%',
    },
    maxHeight: {
        maxHeight: 250,
    },
}));

const LocationMultiSelect = ({
    input: { value: rawValue, onChange, name },
    disabled,
    data,
    error,
    extra,
    showAll,
}) => {
    const classes = useStyles();
    const isFranchise = useIsFranchise();
    const employees = useSelector(selectEmployeesById);
    const empId = useGetUserEmplyeeId();
    const active_user_employee = employees[empId];

    if (error || !data) {
        return <div className="alert alert-danger">Fehler beim Laden der Standorte</div>;
    }

    const franchiseLocation = isFranchise
        ? data.find(location => location.id === active_user_employee?.locationId)
        : null;

    // Franchise users only ever get their own location as an option, exactly
    // like the Formik version. Everyone else can pick from the active
    // locations (or all of them, if showAll), plus an "unassigned" pseudo option.
    const options = isFranchise
        ? franchiseLocation
            ? [{ label: franchiseLocation.matchCode, value: franchiseLocation.id }]
            : []
        : [
              ...data
                  .filter(location => showAll || !location.status || location.status === 'active')
                  .map(location => ({ label: location.matchCode, value: location.id })),
              ...extra.map(location => ({
                  label: location.matchCode || location.name,
                  value: location.id,
              })),
          ];

    const value = isFranchise
        ? franchiseLocation
            ? [franchiseLocation.id]
            : []
        : Array.isArray(rawValue)
        ? rawValue
        : rawValue
        ? [rawValue]
        : [];

    const selectedLabels = options
        .filter(option => value.some(v => String(v) === String(option.value)))
        .map(option => option.label)
        .join(', ');

    return (
        <FormControl variant="outlined" className={classes.fullWidth} size="small">
            <Select
                multiple
                displayEmpty
                value={value}
                onChange={e => onChange(e.target.value)}
                renderValue={() => selectedLabels}
                input={<OutlinedInput />}
                fullWidth
                disabled={disabled || isFranchise}
                MenuProps={{ classes: { paper: classes.maxHeight } }}
                data-cy={`select-${name}`}
            >
                {options.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={value.some(v => String(v) === String(option.value))} />
                        <ListItemText primary={option.label} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

LocationMultiSelect.propTypes = {
    input: InputPropType.isRequired,
    data: SelectDataPropType,
    error: ApiErrorPropType,
    disabled: PropTypes.bool,
    extra: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string,
            isDivider: PropTypes.bool,
        })
    ),
    showAll: PropTypes.bool,
};

LocationMultiSelect.defaultProps = {
    data: null,
    error: null,
    disabled: false,
    extra: [],
    showAll: false,
};

export default connect(state => ({
    data: state.location.entities,
    error: state.location.error,
}))(LocationMultiSelect);
